import { jsPDF } from 'jspdf'

/**
 * The two safeguarding forms, rendered as the paper documents they replace.
 *
 * Drawn with jsPDF's text API rather than html2canvas: these are documents, not
 * screenshots. A canvas render would ship a fuzzy bitmap that cannot be searched,
 * cannot be selected, and prints badly — and this is a form that ends up in a
 * safeguarding file, printed and filed.
 *
 * jsPDF is already a dependency (Newsletter uses it). Nothing new is added: the
 * deploy mounts code over a pre-built image, so a fresh package.json entry would
 * look fine locally and simply be missing in the container.
 */
const ORANGE = [244, 81, 30]
const INK = [26, 26, 26]
const GREY = [120, 120, 120]

const M = 18 // page margin, mm
const W = 210 // A4 width, mm
const H = 297 // A4 height, mm

const fmtDate = (iso, withTime) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  const opts = { day: '2-digit', month: 'short', year: 'numeric' }
  if (withTime) {
    opts.hour = '2-digit'
    opts.minute = '2-digit'
  }
  return d.toLocaleString('en-IN', opts)
}

const header = (doc, title, centre) => {
  doc.setFillColor(...ORANGE)
  doc.rect(0, 0, W, 4, 'F')

  doc.setTextColor(...ORANGE)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('early years', M, 16)

  doc.setTextColor(...INK)
  doc.setFontSize(16)
  doc.text(title, M, 26)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(...GREY)
  doc.text(`Oh Early Years  ·  ${centre || '—'}`, M, 33)
  doc.setDrawColor(220)
  doc.line(M, 37, W - M, 37)
  doc.setTextColor(...INK)
  return 46
}

/** Drawn signatures, side by side, each with its role underneath. */
const signatures = (doc, items, y) => {
  const usable = W - M * 2
  const boxW = usable / items.length - 4
  const boxH = 22
  items.forEach((item, i) => {
    const x = M + i * (boxW + 4)
    doc.setDrawColor(210)
    doc.rect(x, y, boxW, boxH)
    if (item.signature) {
      try {
        doc.addImage(item.signature, 'PNG', x + 1, y + 1, boxW - 2, boxH - 2)
      } catch (e) {
        // A corrupt data URL must not take the whole document down — an
        // unsigned box is a truthful document; no document is not.
      }
    }
    doc.setFontSize(8)
    doc.setTextColor(...GREY)
    doc.text(String(item.label).toUpperCase(), x, y + boxH + 4)
    if (item.name) {
      doc.setFontSize(10)
      doc.setTextColor(...INK)
      doc.text(String(item.name), x, y + boxH + 9)
    }
  })
  return y + boxH + 16
}

const footer = (doc, note) => {
  doc.setFontSize(8)
  doc.setTextColor(...GREY)
  doc.text(note, M, 287)
}

/* -------------------------------------------------------------------------
 * The Accident / Incident Report, rendered in the SAME design language as the
 * Underage Consent letter (per request: "copy the underage design, content the
 * same"): openhouse wordmark + corner leaves, the AvenirRounded title, and the
 * same underlined fill-in blanks. Authored on the shared 1414 x 2000 design
 * canvas and scaled into A4. Brand assets (leaves, logo, fonts) are lazy-imported
 * from underageConsentAssets, so this function is async like buildUnderagePdf.
 * Page 1 = the incident; page 2 = follow-up + signatures.
 * ---------------------------------------------------------------------------- */
export const buildIncidentPdf = async (row) => {
  const A = await import('./underageConsentAssets')
  const d = row.data || {}

  const A4W = 595.28
  const A4H = 841.89
  const KX = A4W / 1414
  const KY = A4H / 2000
  const px = (v) => v * KX
  const py = (v) => v * KY
  const pf = (v) => v * KX

  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  doc.addFileToVFS('AvRDemi.ttf', A.FONT_DEMI)
  doc.addFont('AvRDemi.ttf', 'AvenirRounded', 'bold')
  doc.addFileToVFS('AvRReg.ttf', A.FONT_REG)
  doc.addFont('AvRReg.ttf', 'AvenirRounded', 'normal')

  const ML = 150 // left margin (design units)
  const MR = 1268 // content right edge (design units)

  const corners = () => {
    doc.addImage(A.LEAF_TL, 'JPEG', px(0), py(0), px(206), py(246))
    doc.addImage(A.LOGO, 'JPEG', px(1030), py(0), px(384), py(300))
    doc.addImage(A.LEAF_BR, 'JPEG', px(1198), py(1763), px(216), py(237))
  }
  const ink = () => doc.setTextColor(38, 33, 27)
  const body = () => {
    doc.setFont('AvenirRounded', 'normal')
    doc.setFontSize(pf(26))
    ink()
  }
  const bold = (t, yy, size) => {
    doc.setFont('AvenirRounded', 'bold')
    doc.setFontSize(pf(size))
    ink()
    doc.text(t, px(ML), py(yy))
  }
  const uline = (x0, x1, yy) => {
    doc.setDrawColor(60)
    doc.setLineWidth(px(1.2))
    doc.line(px(x0), py(yy), px(x1), py(yy))
  }
  // "Label: value" — the label BOLD like the paper AIR form, the value in normal
  // on a ruled line that starts after the label.
  const field = (label, value, yy, x0, x1) => {
    doc.setFont('AvenirRounded', 'bold')
    doc.setFontSize(pf(26))
    ink()
    doc.text(label, px(x0), py(yy))
    const vX0 = x0 + doc.getTextWidth(label) / KX + 16
    doc.setFont('AvenirRounded', 'normal')
    doc.text(String(value == null ? '' : value), px(vX0), py(yy))
    uline(vX0, x1, yy + 16)
  }
  // A bold question with the answer wrapped over `rows` ruled writing lines.
  const writeBlock = (label, value, yy, rows) => {
    bold(label, yy, 26)
    body()
    const lines = doc.splitTextToSize(String(value || ''), px(MR - ML))
    const n = Math.max(rows, lines.length)
    let ly = yy + 74
    for (let i = 0; i < n; i += 1) {
      if (lines[i]) doc.text(lines[i], px(ML), py(ly - 10))
      uline(ML, MR, ly)
      ly += 84
    }
    return ly
  }
  // Yes/No answered by a ticked box against the chosen option.
  const tickRow = (label, value, yy) => {
    doc.setFont('AvenirRounded', 'bold')
    doc.setFontSize(pf(26))
    ink()
    doc.text(label, px(ML), py(yy))
    let cx = px(ML) + doc.getTextWidth(label) + px(26)
    const opt = (t, on) => {
      const s = px(30)
      const by = py(yy) - s * 0.82
      doc.setDrawColor(60)
      doc.setLineWidth(px(1.4))
      doc.rect(cx, by, s, s)
      if (on) {
        doc.setDrawColor(38, 33, 27)
        doc.setLineWidth(px(2.6))
        doc.line(cx + s * 0.2, by + s * 0.55, cx + s * 0.42, by + s * 0.8)
        doc.line(cx + s * 0.42, by + s * 0.8, cx + s * 0.82, by + s * 0.22)
      }
      body()
      doc.text(t, cx + s + px(12), py(yy))
      cx = cx + s + px(12) + doc.getTextWidth(t) + px(64)
    }
    opt('Yes', Boolean(value))
    opt('No', !value)
  }
  // One signatory: the drawn signature sits ON the rule, the person's printed
  // name goes directly under it, and the role caption under that. Educator and
  // centre director each get their own column, so a signature and the name that
  // belongs to it are never read as two separate slots.
  const sigSlot = (label, x0, x1, yy, opts) => {
    uline(x0, x1, yy)
    if (opts.signature) {
      try {
        doc.addImage(
          opts.signature,
          'PNG',
          px(x0 + 15),
          py(yy - 112),
          px(320),
          py(100)
        )
      } catch (e) {
        // an unsigned line is a truthful document; a broken image is not
      }
    }
    let capY = yy + 52
    if (opts.name) {
      body()
      doc.text(String(opts.name), px(x0), py(yy + 46))
      capY = yy + 96
    }
    doc.setFont('AvenirRounded', 'bold')
    doc.setFontSize(pf(26))
    ink()
    doc.text(label, px(x0), py(capY))
  }

  // ---- Page 1: the incident ----
  corners()
  // Title: smaller and centred, like the original paper AIR form.
  doc.setFont('AvenirRounded', 'bold')
  ink()
  doc.setFontSize(pf(46))
  doc.text('accident incident report (air)', px(707), py(345), { align: 'center' })

  bold('Oh Early Years', 470, 30)

  field('Centre:', row.centre_name, 560, ML, 700)
  field(
    'Date:',
    fmtDate(row.submitted_at || row.created || row.occurred_at),
    560,
    755,
    MR
  )

  bold('Child Details', 655, 30)
  field('Name:', row.child_name, 745, ML, 700)
  field('Program:', row.program, 745, 755, MR)

  field('Date and Time of Incident:', fmtDate(row.occurred_at, true), 830, ML, MR)
  field('Location of the incident:', row.location, 908, ML, MR)

  writeBlock(
    'What happened? : Brief description of the incident',
    d.what_happened,
    992,
    4
  )

  bold('Action Taken:', 1400, 30)
  tickRow('Was first aid given? :', row.first_aid_given, 1488)
  writeBlock('What did we do? :', d.what_we_did, 1560, 2)

  // ---- Page 2: follow-up + signatures ----
  doc.addPage()
  corners()
  bold('Follow Up', 420, 30)
  tickRow('Were Parents notified? :', row.parents_notified, 520)
  writeBlock('How did we inform the parent :', d.how_parent_informed, 600, 2)
  writeBlock('What did the parent say :', d.parent_response, 850, 2)

  // Educator on the left, centre director on the right — each with their own
  // name printed under their signature.
  sigSlot('Signatures of Educator(s)', ML, 700, 1320, {
    signature: row.educator_signature,
    name: d.educator_names
  })
  sigSlot("Centre Director's Signature", 770, MR, 1320, {
    signature: row.director_signature,
    name: d.director_name
  })

  body()
  doc.setFontSize(pf(18))
  doc.setTextColor(120, 120, 120)
  doc.text(
    row.submitted
      ? `Submitted ${fmtDate(row.submitted_at, true)} · Alfred`
      : 'DRAFT — not yet submitted · Alfred',
    px(ML),
    py(1900)
  )

  return doc
}

/**
 * The Underage Consent letter, rendered pixel-faithful to the approved design
 * ("Underage Consent Letter & Parent Undertaking"): openhouse wordmark, the two
 * corner leaves, the AvenirNextRoundedPro title, and the letter with the child's
 * real numbers filled into the blanks + the parent's signature.
 *
 * The design was authored on a 1414 x 2000 pt canvas; we build at A4 and scale
 * every coordinate into it, so positions map straight off the original artwork.
 * Brand assets (leaves, logo, fonts) are lazy-imported so their ~170KB only loads
 * when a consent PDF is actually generated — hence this function is async.
 */
export const buildUnderagePdf = async (row) => {
  const A = await import('./underageConsentAssets')

  const A4W = 595.28
  const A4H = 841.89
  const KX = A4W / 1414
  const KY = A4H / 2000
  const px = (v) => v * KX
  const py = (v) => v * KY
  const pf = (v) => v * KX // font sizes scale with the page

  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  doc.addFileToVFS('AvRDemi.ttf', A.FONT_DEMI)
  doc.addFont('AvRDemi.ttf', 'AvenirRounded', 'bold')
  doc.addFileToVFS('AvRReg.ttf', A.FONT_REG)
  doc.addFont('AvRReg.ttf', 'AvenirRounded', 'normal')
  doc.setTextColor(38, 33, 27)

  // Corner leaves + wordmark, placed at their exact positions on the page.
  const art = (data, x0, y0, x1, y1) =>
    doc.addImage(data, 'JPEG', px(x0), py(y0), px(x1 - x0), py(y1 - y0))
  art(A.LEAF_TL, 0, 0, 206, 246)
  art(A.LOGO, 1030, 0, 1414, 300)
  art(A.LEAF_BR, 1198, 1763, 1414, 2000)

  const ML = 150 // left margin (design units)
  const CW = 1268 - ML // content width

  const uline = (x0, x1, yy) => {
    doc.setDrawColor(60)
    doc.setLineWidth(px(1.3))
    doc.line(px(x0), py(yy), px(x1), py(yy))
  }

  doc.setFont('AvenirRounded', 'bold')
  doc.setFontSize(pf(60))
  doc.text('underage consent letter and parent', px(ML), py(358))
  doc.text('undertaking', px(ML), py(448))

  doc.setFontSize(pf(30))
  doc.text('Oh Early Years', px(ML), py(520))

  doc.setFont('AvenirRounded', 'normal')
  doc.setFontSize(pf(26))

  // Centre / Date
  doc.text('Centre:', px(ML), py(612))
  doc.text(row.centre_name || '', px(268), py(608))
  uline(255, 700, 620)
  doc.text('Date:', px(755), py(612))
  doc.text(fmtDate(row.signed_at || row.created), px(850), py(608))
  uline(835, 1190, 620)

  // Subject — the grade value underlined like a filled-in blank.
  const sub = 'Subject: Consent and Undertaking for Enrolling Underage Child in '
  doc.text(sub, px(ML), py(700))
  const gx = px(ML) + doc.getTextWidth(sub) + px(6)
  const gradeVal = row.grade || ''
  doc.text(gradeVal, gx, py(700))
  const gw = doc.getTextWidth(gradeVal)
  doc.setDrawColor(60)
  doc.setLineWidth(px(1.1))
  doc.line(gx, py(712), gx + gw, py(712))
  doc.text('Grade', gx + gw + px(14), py(700))

  // Dear <name>, — the addressee underlined. The centre director is no longer
  // collected on the form, so fall back to a neutral salutation rather than
  // printing "Dear ," with a blank underline.
  doc.text('Dear ', px(ML), py(795))
  const dnx = px(ML) + doc.getTextWidth('Dear ')
  const dearVal = row.addressed_to || 'Sir/Madam'
  doc.text(dearVal, dnx, py(795))
  const dw = doc.getTextWidth(dearVal)
  doc.line(dnx, py(807), dnx + dw, py(807))
  doc.text(',', dnx + dw, py(795))

  // The letter body: prose with every VARIABLE value underlined, like a filled
  // blank. days_underage is Alfred's computed claim the parent is signing, so it
  // reads as on screen. Rendered as inline segments (fixed text vs. value) with a
  // small word-wrap, so each value keeps a continuous underline even mid-line.
  const days =
    row.days_underage === null || row.days_underage === undefined
      ? ''
      : String(row.days_underage)
  const paras = [
    [
      { t: 'I, ' },
      { t: row.parent_name || '', u: true },
      { t: ', parent/guardian of ' },
      { t: row.child_name || '', u: true },
      { t: ', understand that the Oh age Policy requires children to be ' },
      { t: row.required_age_years || '', u: true },
      { t: ' years old for ' },
      { t: row.program || '', u: true },
      { t: ' program. My child is born on ' },
      { t: fmtDate(row.child_dob), u: true },
      { t: ' and is ' },
      { t: days, u: true },
      { t: ' days underage for this grade.' }
    ],
    [
      { t: 'Despite this, I request that my child be enrolled in the ' },
      { t: row.grade || '', u: true },
      { t: ' grade at Oh Early Years for the ' },
      { t: row.academic_year || '', u: true },
      {
        t: ' academic year. I take full responsibility for this decision and any impacts it may have on my child’s progress.'
      }
    ],
    [
      {
        t: 'Additionally, I agree that my child will repeat a grade before moving on to Kindergarten 1 to meet the age requirements.'
      }
    ],
    [{ t: 'Thank you for your understanding and consideration.' }]
  ]

  const flowX0 = px(ML)
  const flowMaxX = px(ML) + px(CW)
  const lineH = py(80)
  const ulOff = py(11)
  const flow = (segments, startDesign) => {
    let cx = flowX0
    let cy = py(startDesign)
    segments.forEach((seg) => {
      const toks = seg.t.match(/\s*\S+\s*/g) || []
      toks.forEach((tok) => {
        const w = doc.getTextWidth(tok)
        if (cx > flowX0 && cx + w > flowMaxX) {
          cx = flowX0
          cy += lineH
        }
        doc.text(tok, cx, cy)
        if (seg.u && tok.trim()) {
          doc.setDrawColor(60)
          doc.setLineWidth(px(1.1))
          doc.line(cx, cy + ulOff, cx + w, cy + ulOff)
        }
        cx += w
      })
    })
    return cy + lineH
  }
  // yy stays in design units so the signature block below is unchanged.
  let yy = 890
  paras.forEach((segments) => {
    yy = flow(segments, yy) / KY + 24
  })
  doc.text('Sincerely,', px(ML), py(yy))

  // Signature block — always below the sign-off so it can never collide.
  const sigY = yy + 126
  const labY = sigY + 48
  uline(150, 560, sigY)
  uline(755, 1165, sigY)
  if (row.parent_signature) {
    try {
      doc.addImage(
        row.parent_signature,
        'PNG',
        px(165),
        py(sigY - 108),
        px(330),
        py(96)
      )
    } catch (e) {
      // A corrupt signature data URL must not take the document down — an
      // unsigned line is a truthful document; no document is not.
    }
  }
  doc.text(row.parent_name || '', px(770), py(sigY - 14))
  doc.setFont('AvenirRounded', 'bold')
  doc.setFontSize(pf(26))
  doc.text('Parent/Guardian Signature', px(150), py(labY))
  doc.text('Parent/Guardian Name', px(755), py(labY))

  return doc
}

const slug = (s) =>
  String(s || 'form')
    .trim()
    .replace(/\s+/g, '_')

export const downloadIncidentPdf = async (row) =>
  (await buildIncidentPdf(row)).save(`AIR_${slug(row.child_name)}.pdf`)

export const downloadUnderagePdf = async (row) =>
  (await buildUnderagePdf(row)).save(
    `Underage_Consent_${slug(row.child_name)}.pdf`
  )

/** For the on-screen preview: the same document, as a blob URL. */
export const incidentPdfUrl = async (row) =>
  (await buildIncidentPdf(row)).output('bloburl').toString()

export const underagePdfUrl = async (row) =>
  (await buildUnderagePdf(row)).output('bloburl').toString()
